/**
 * Activity Log (Phase 6 / item #12).
 *
 * Streams audit_log entries for the caller's company. Each AIP invocation
 * emits a row with `result` (ok|permanent), `duration_ms`, `model_id`, and
 * — on failure — `error_class`/`error_message`. Useful for diagnosing
 * "AIP returned unknown" or "ingest stalled" without digging into CloudWatch.
 *
 * Filter by report id is optional; if omitted shows the company's full feed.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Header,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  TextFilter,
} from "@cloudscape-design/components";
import { api } from "@/core/api/client";
import { formatET } from "@/core/ui/time";

interface ActivityEvent {
  id: string;
  event_type: string;
  target_type: string | null;
  target_id: string | null;
  metadata: {
    aip?: string;
    model_id?: string;
    duration_ms?: number;
    result?: string;
    outcome?: string;
    error_class?: string;
    error_message?: string;
    report_id?: string;
  };
  created_at: string;
}

const PAGE_SIZE = 25;

function resultBadge(event: ActivityEvent): { color: "green" | "red" | "grey"; label: string } {
  const result = event.metadata.result ?? "";
  if (event.event_type.startsWith("aip.")) {
    if (result === "ok") return { color: "green", label: "OK" };
    if (result) return { color: "red", label: result };
  }
  return { color: "grey", label: event.event_type };
}

export function ActivityLog() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ events: ActivityEvent[] }>("/activity");
      setEvents(r.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? events.filter(
        (e) =>
          e.event_type.toLowerCase().includes(q) ||
          (e.metadata.aip ?? "").toLowerCase().includes(q) ||
          (e.metadata.error_class ?? "").toLowerCase().includes(q) ||
          (e.metadata.report_id ?? "").toLowerCase().includes(q)
      )
    : events;

  const start = (page - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);

  if (loading && !events.length) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner /> Loading activity…
      </Box>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h1"
          counter={`(${events.length})`}
          description="AI invocations, ingest events, and system actions. Most recent first."
          actions={<Button onClick={refresh} iconName="refresh" disabled={loading} />}
        >
          Activity log
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Failed to load activity">
            {error}
          </Alert>
        )}
        <Table
          items={visible}
          variant="embedded"
          filter={
            <TextFilter
              filteringText={filter}
              filteringPlaceholder="Filter by AIP, event type, error class, report"
              onChange={({ detail }) => {
                setFilter(detail.filteringText);
                setPage(1);
              }}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={page}
              pagesCount={Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
            />
          }
          columnDefinitions={[
            {
              id: "when",
              header: "When",
              cell: (e) => formatET(new Date(e.created_at)),
              width: 180,
            },
            {
              id: "aip",
              header: "AIP / event",
              cell: (e) => (e.metadata.aip ? <Box variant="strong">{e.metadata.aip}</Box> : e.event_type),
              width: 200,
            },
            {
              id: "result",
              header: "Result",
              cell: (e) => {
                const b = resultBadge(e);
                return <Badge color={b.color}>{b.label}</Badge>;
              },
              width: 110,
            },
            {
              id: "duration",
              header: "Duration",
              cell: (e) =>
                typeof e.metadata.duration_ms === "number" ? `${e.metadata.duration_ms} ms` : "—",
              width: 110,
            },
            {
              id: "outcome",
              header: "Outcome",
              cell: (e) =>
                e.metadata.outcome ? (
                  <Box variant="small">{e.metadata.outcome}</Box>
                ) : (
                  <Box variant="small" color="text-body-secondary">
                    —
                  </Box>
                ),
              width: 260,
            },
            {
              id: "model",
              header: "Model",
              cell: (e) => e.metadata.model_id ?? "—",
              width: 280,
            },
            {
              id: "detail",
              header: "Detail",
              cell: (e) =>
                e.metadata.error_message ? (
                  <Box color="text-status-error" variant="small">
                    {e.metadata.error_class}: {e.metadata.error_message}
                  </Box>
                ) : e.metadata.report_id ? (
                  <Box variant="small" color="text-body-secondary">
                    report {e.metadata.report_id.slice(0, 8)}…
                  </Box>
                ) : (
                  "—"
                ),
            },
          ]}
          empty={
            <Box textAlign="center" color="text-body-secondary" padding="m">
              No activity yet. AIP calls will appear here as reports are ingested.
            </Box>
          }
        />
      </SpaceBetween>
    </Container>
  );
}
